import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Bell, UserPlus, Heart, MessageCircle, Package, Check, X } from 'lucide-react';
import { API, authFetch } from '@/App';
import { toast } from 'sonner';

export default function NotificationsPage() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNotifications();
    markAllAsRead();
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await authFetch(`${API}/notifications`);
      if (response.ok) {
        const data = await response.json();
        setNotifications(data);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAllAsRead = async () => {
    try {
      await authFetch(`${API}/notifications/mark-read`, {
        method: 'POST',
      });
    } catch (error) {
      console.error('Error marking notifications as read:', error);
    }
  };

  const handleAcceptFollow = async (notificationId, requestId) => {
    try {
      const response = await authFetch(`${API}/follow-requests/${requestId}/accept`, {
        method: 'POST',
      });
      if (response.ok) {
        toast.success('Follow request accepted');
        setNotifications(prev => prev.map(n => 
          n.notification_id === notificationId 
            ? { ...n, action_taken: 'accepted' }
            : n
        ));
      } else {
        const errData = await response.json().catch(() => ({}));
        if (response.status === 404) {
          toast.info('Request already handled');
          setNotifications(prev => prev.map(n => 
            n.notification_id === notificationId 
              ? { ...n, action_taken: 'accepted' }
              : n
          ));
        } else {
          toast.error(errData.detail || 'Failed to accept request');
        }
      }
    } catch (error) {
      console.error('Error accepting follow request:', error);
      toast.error('Failed to accept request');
    }
  };

  const handleDeclineFollow = async (notificationId, requestId) => {
    try {
      const response = await authFetch(`${API}/follow-requests/${requestId}/decline`, {
        method: 'POST',
      });
      if (response.ok) {
        toast.success('Follow request declined');
        setNotifications(prev => prev.map(n => 
          n.notification_id === notificationId 
            ? { ...n, action_taken: 'declined' }
            : n
        ));
      } else {
        if (response.status === 404) {
          toast.info('Request already handled');
          setNotifications(prev => prev.map(n => 
            n.notification_id === notificationId 
              ? { ...n, action_taken: 'declined' }
              : n
          ));
        } else {
          toast.error('Failed to decline request');
        }
      }
    } catch (error) {
      console.error('Error declining follow request:', error);
      toast.error('Failed to decline request');
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'follow_request':
      case 'new_follower':
      case 'follow_accepted':
        return <UserPlus className="w-5 h-5 text-primary" />;
      case 'like':
        return <Heart className="w-5 h-5 text-red-500" />;
      case 'comment':
        return <MessageCircle className="w-5 h-5 text-blue-500" />;
      case 'new_product':
      case 'brand_update':
        return <Package className="w-5 h-5 text-green-500" />;
      default:
        return <Bell className="w-5 h-5 text-muted-foreground" />;
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  const handleNotificationClick = (notification) => {
    if (notification.link) {
      navigate(notification.link);
    } else if (notification.from_user_id) {
      navigate(`/profile/${notification.from_user_id}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-border">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center">
          <button data-testid="back-btn" onClick={() => navigate(-1)} className="p-2 hover:bg-primary/10 rounded-full transition-colors">
            <ArrowLeft className="w-6 h-6" />
          </button>
          <h1 className="text-xl font-bold ml-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Notifications</h1>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 py-6">
        {notifications.length === 0 ? (
          <div className="text-center py-12">
            <Bell className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">No notifications yet</p>
            <p className="text-sm text-muted-foreground mt-1">When you get notifications, they'll show up here</p>
          </div>
        ) : (
          <div className="space-y-2">
            {notifications.map(notification => (
              <div
                key={notification.notification_id}
                data-testid={`notification-item-${notification.notification_id}`}
                onClick={() => handleNotificationClick(notification)}
                className={`p-4 rounded-xl border transition-colors cursor-pointer hover:bg-primary/10 ${
                  notification.is_read ? 'bg-white border-border' : 'bg-primary/5 border-primary/20'
                }`}
              >
                <div className="flex items-start gap-3">
                  {/* Icon or User Picture */}
                  {notification.from_user_picture ? (
                    <button onClick={() => navigate(`/profile/${notification.from_user_id}`)}>
                      <img 
                        src={notification.from_user_picture} 
                        alt="" 
                        className="w-10 h-10 rounded-full"
                      />
                    </button>
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      {getNotificationIcon(notification.type)}
                    </div>
                  )}
                  
                  <div className="flex-1">
                    <p className="text-sm">
                      {notification.from_username && (
                        <button 
                          onClick={() => navigate(`/profile/${notification.from_user_id}`)}
                          className="font-bold hover:underline"
                        >
                          @{notification.from_username}
                        </button>
                      )}
                      {' '}{notification.message}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {formatTime(notification.created_at)}
                    </p>
                    
                    {/* Action buttons for follow requests */}
                    {notification.type === 'follow_request' && !notification.action_taken && (
                      <div className="flex gap-2 mt-3" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => handleAcceptFollow(notification.notification_id, notification.reference_id)}
                          className="flex items-center gap-1 px-4 py-2 bg-primary text-white rounded-full text-sm font-medium hover:bg-primary/90 transition-colors"
                        >
                          <Check className="w-4 h-4" />
                          Accept
                        </button>
                        <button
                          onClick={() => handleDeclineFollow(notification.notification_id, notification.reference_id)}
                          className="flex items-center gap-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-full text-sm font-medium hover:bg-gray-200 transition-colors"
                        >
                          <X className="w-4 h-4" />
                          Decline
                        </button>
                      </div>
                    )}
                    
                    {notification.action_taken && (
                      <p className="text-xs text-muted-foreground mt-2">
                        Request {notification.action_taken}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
